import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  MessageCircle, Brain, Settings as SettingsIcon,
  Sparkles, Home as HomeIcon, PlugZap, Zap,
  Sunrise, AudioLines, Library, Users,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import FocusMode from "@/components/FocusMode";
import OnboardingFlow from "@/components/onboarding/OnboardingFlow";
import Sidebar from "@/components/appShell/Sidebar";
import MainHeader from "@/components/appShell/MainHeader";
import RightPanel from "@/components/appShell/RightPanel";
import RammyPingHost, { pushRammyPing } from "@/components/RammyPingToast";
import {
  HomeScreen, ChatScreen, MemoriesScreen, RemindersScreen, SettingsScreen,
  IntegrationsScreen, InboxScreen, EmailsScreen, FocusScreen, CalendarScreen,
  SentRepliesScreen, PersonalityScreen, VoiceScreen, LanguageScreen,
  PrivacyScreen, PlansScreen, FamilyScreen, FindScreen, OtaScreen, PairScreen,
  StudioScreen, CreationsScreen, CastScreen, CreditsScreen, RitualsScreen, CreativeDnaScreen, CopyStudioScreen, MyVoiceScreen,
} from "@/pages/app/screens";

/**
 * AppShell — full-page, desktop-class productivity workspace.
 * Three-column layout:
 *   • Left  : navigation sidebar (collapsible)
 *   • Main  : primary screen (Chat by default, or any tab)
 *   • Right : Today panel (calendar preview, focus card, inbox teaser, device stats)
 */
export default function AppShell() {
  const { user, logout, refresh } = useAuth();
  const nav = useNavigate();

  const [screen, setScreen] = useState(() => {
    // Land straight in the conversation when we arrive with ?chat=<id>
    // (a claimed homepage chat) instead of dropping the user on the dashboard.
    try {
      return new URLSearchParams(window.location.search).get("chat") ? "chat" : "home";
    } catch (_) {
      return "home";
    }
  });
  const [collapsed, setCollapsed] = useState(false);
  // Mobile drawer state — desktop ignores this flag; sidebar always visible there.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [device, setDevice] = useState(null);
  const [personalities, setPersonalities] = useState([]);
  const [voices, setVoices] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [plans, setPlans] = useState([]);
  const [chatExpression, setChatExpression] = useState("idle");
  const [focusModeOpen, setFocusModeOpen] = useState(false);
  // Conversation id the ChatScreen should auto-open on next mount. Set by the
  // Home "Continue where you left off" card, or by `?chat=<id>` after a
  // sign-in that adopted the visitor's homepage conversation; consumed once
  // and cleared.
  const [pendingConversationId, setPendingConversationId] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get("chat") || null;
    } catch (_) {
      return null;
    }
  });

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    // Consume ?chat=<id> from the URL so a refresh doesn't re-open it.
    try {
      if (new URLSearchParams(window.location.search).get("chat") && window.history?.replaceState) {
        window.history.replaceState(null, "", window.location.pathname);
      }
    } catch (_) { /* no-op */ }
    api.get("/device").then(r => setDevice(r.data)).catch(() => {});
    api.get("/catalog/personalities").then(r => setPersonalities(r.data)).catch(() => {});
    api.get("/catalog/voices").then(r => setVoices(r.data)).catch(() => {});
    api.get("/catalog/languages").then(r => setLanguages(r.data)).catch(() => {});
    api.get("/catalog/plans").then(r => setPlans(r.data)).catch(() => {});
  }, []);

  // Proactive-ping poller — Rammy's "presence" toasts. Runs while the app
  // is mounted (any screen) and dedupes by seed/reminder id so the same
  // ping never fires twice. We poll every 60 s + fire immediately on mount
  // so the user gets a friendly hello if Rammy has been waiting on something.
  useEffect(() => {
    if (!user) return;
    let stopped = false;
    const fired = new Set();

    const poll = async () => {
      if (stopped) return;
      try {
        const [checkins, dueSummary] = await Promise.all([
          api.get("/checkins/pending").then(r => r.data || []).catch(() => []),
          api.get("/reminders/due-summary").then(r => r.data).catch(() => null),
        ]);
        // Emotional check-in seeds — warmest surface.
        for (const seed of checkins) {
          const key = `checkin:${seed.id}`;
          if (fired.has(key)) continue;
          fired.add(key);
          pushRammyPing({
            id: key,
            title: "Checking in",
            body: seed.body || seed.line || seed.text ||
                  "Just wanted to see how you're doing today.",
            mood: "speaking",
            action: {
              label: "Talk to Rammy",
              onClick: () => setScreen("chat"),
            },
          });
        }
        // Reminders that came due while the user was elsewhere in the app.
        // The /reminders/due-summary endpoint returns `due_now` — we surface
        // the count only (not the raw text) so this stays a "presence" cue
        // rather than a to-do dump. The CTA opens the reminders list.
        const dueCount = dueSummary?.due_now ?? 0;
        if (dueCount > 0) {
          const asOf = dueSummary?.as_of || "";
          const dayBucket = asOf.slice(0, 13); // dedupe by hour bucket
          const key = `due:${dayBucket}:${dueCount}`;
          if (!fired.has(key)) {
            fired.add(key);
            const firstItem = dueSummary?.preview?.[0];
            pushRammyPing({
              id: key,
              title: dueCount === 1 ? "One reminder just came due" : `${dueCount} reminders just came due`,
              body: firstItem?.title
                    ? `“${firstItem.title}” — I'll walk through this whenever you're ready.`
                    : "I'll walk through them with you whenever you're ready.",
              mood: "idle",
              action: {
                label: "Open reminders",
                onClick: () => setScreen("reminders"),
              },
            });
          }
        }
      } catch (_) { /* silent — presence should never surface errors */ }

      // Low-balance heads-up. The server only ever creates one of these a
      // month; we ack it so it never reappears.
      try {
        const { data } = await api.get("/credits/nudge");
        const nudge = data?.nudge;
        if (nudge && !fired.has(`credits:${nudge.id}`)) {
          fired.add(`credits:${nudge.id}`);
          pushRammyPing({
            id: `credits:${nudge.id}`,
            title: nudge.title,
            body: nudge.body,
            mood: "idle",
            action: { label: "Top up", onClick: () => setScreen("credits") },
          });
          api.post(`/credits/nudge/${nudge.id}/ack`).catch(() => {});
        }
      } catch (_) { /* a balance heads-up must never break the app */ }
    };

    // Small delay on first fire so the sidebar has a chance to settle in
    // before Rammy greets — otherwise the toast collides with route mounting.
    const first = setTimeout(poll, 1200);
    const interval = setInterval(poll, 60_000);
    return () => { stopped = true; clearTimeout(first); clearInterval(interval); };
  }, [user]);

  // Handle OAuth callback returns from AppSimulator behaviour (Google Calendar)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("connected") !== "calendar") return;
    const status = params.get("status");
    const detail = params.get("detail");
    if (status === "ok") {
      toast.success("Google Calendar connected — Rammy can now read your events.");
      refresh?.();
      setScreen("calendar");
    } else {
      const messages = {
        invalid_state: "That sign-in link expired. Try connecting again.",
        state_expired: "That sign-in link expired. Try connecting again.",
        token_exchange_failed: "Google couldn't complete the handshake. Try again.",
        no_refresh_token: "Google didn't share offline access. Visit myaccount.google.com/permissions, remove Rammy, then retry.",
        missing_code_or_state: "Google returned an incomplete response.",
      };
      toast.error(messages[detail] || `Couldn't connect Google Calendar${detail ? ` — ${detail}` : ""}`);
    }
    window.history.replaceState({}, "", window.location.pathname);
  }, []);

  // Cross-component navigation via a custom event (e.g. the inline
  // ReminderCard in chat asks to open the Reminders screen).
  useEffect(() => {
    const onNav = (e) => {
      const target = e?.detail?.screen;
      if (target && typeof target === "string") setScreen(target);
    };
    window.addEventListener("rammy-navigate", onNav);
    return () => window.removeEventListener("rammy-navigate", onNav);
  }, []);

  const go = (s) => setScreen(s);
  // Home hands off a conversation id to resume. We route through this helper
  // so the id gets injected into ChatScreen the instant we flip screens.
  const resumeConversation = (id) => {
    setPendingConversationId(id);
    setScreen("chat");
  };

  const primaryNav = [
    { id: "home",         icon: <HomeIcon size={18}/>,      label: "Today" },
    { id: "reminders",    icon: <Zap size={18}/>,           label: "Reminders" },
    { id: "memories",     icon: <Brain size={18}/>,         label: "Rings" },
    { id: "rituals",      icon: <Sunrise size={18}/>,       label: "Rituals" },
    { id: "studio",       icon: <AudioLines size={18}/>,    label: "Studio" },
    { id: "creations",    icon: <Library size={18}/>,       label: "Creations" },
    { id: "cast",         icon: <Users size={18}/>,         label: "Cast" },
    { id: "integrations", icon: <PlugZap size={18}/>,       label: "Connections" },
  ];

  // The model picker ("Rammy's brain") was retired 26 Aug 2026 — Rammy has
  // one mind, so there is nothing here for the user to choose.
  const secondaryNav = [
    { id: "credits",  icon: <Sparkles size={18}/>,      label: "Credits" },
    { id: "settings", icon: <SettingsIcon size={18}/>, label: "Settings" },
  ];

  // Which screens render the right context panel? Hide it for full-canvas views.
  const showRightPanel = ["home"].includes(screen);

  const renderScreen = () => {
    const props = { user, device, setDevice, go, refresh, resumeConversation };
    switch (screen) {
      case "chat":         return <ChatScreen user={user} refresh={refresh} expression={chatExpression} setExpression={setChatExpression} initialConversationId={pendingConversationId} onConsumeInitial={() => setPendingConversationId(null)} go={go}/>;
      case "home":         return <HomeScreen {...props} />;
      case "memories":     return <MemoriesScreen/>;
      case "reminders":    return <RemindersScreen/>;
      case "settings":     return <SettingsScreen {...props}/>;
      case "personality":  return <PersonalityScreen personalities={personalities} user={user} refresh={refresh} go={go}/>;
      case "voice":        return <VoiceScreen voices={voices} user={user} refresh={refresh} go={go}/>;
      case "language":     return <LanguageScreen languages={languages} user={user} refresh={refresh} go={go}/>;
      case "privacy":      return <PrivacyScreen device={device} setDevice={setDevice} go={go}/>;
      case "plans":        return <PlansScreen plans={plans} user={user} go={go}/>;
      case "family":       return <FamilyScreen go={go}/>;
      case "find":         return <FindScreen device={device} go={go}/>;
      case "ota":          return <OtaScreen device={device} go={go}/>;
      case "pair":         return <PairScreen go={go}/>;
      case "creative-dna": return <CreativeDnaScreen go={go}/>;
      case "copy-studio":  return <CopyStudioScreen go={go}/>;
      case "calendar":     return <CalendarScreen user={user} refresh={refresh} go={go}/>;
      case "integrations": return <IntegrationsScreen user={user} refresh={refresh} go={go}/>;
      case "inbox":        return <InboxScreen user={user} go={go}/>;
      case "emails":       return <EmailsScreen user={user} go={go}/>;
      case "focus":        return <FocusScreen user={user} go={go}/>;
      case "sent-replies": return <SentRepliesScreen user={user} go={go}/>;
      case "studio":       return <StudioScreen user={user}/>;
      case "creations":    return <CreationsScreen go={go}/>;
      case "cast":         return <CastScreen/>;
      case "credits":      return <CreditsScreen go={go}/>;
      case "rituals":      return <RitualsScreen user={user} go={go} refresh={refresh}/>;
      case "myvoice":      return <MyVoiceScreen go={go}/>;
      default:             return <ChatScreen user={user} expression={chatExpression} setExpression={setChatExpression} initialConversationId={pendingConversationId} onConsumeInitial={() => setPendingConversationId(null)}/>;
    }
  };

  // Chat is a full-flex layout; other screens need padded scroll container
  const isFullFlex = screen === "chat" || screen === "studio";

  return (
    <div className="h-screen w-screen flex bg-[var(--rammy-bg)] text-[var(--rammy-text)] overflow-hidden" data-testid="app-shell">
      <Sidebar
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        screen={screen}
        setScreen={(s) => { setScreen(s); setMobileNavOpen(false); }}
        user={user}
        isAdmin={isAdmin}
        primaryNav={primaryNav}
        secondaryNav={secondaryNav}
        onLogout={async () => { await logout(); nav("/"); }}
        mobileOpen={mobileNavOpen}
        onCloseMobile={() => setMobileNavOpen(false)}
      />

      <main className={`flex-1 min-w-0 flex flex-col ${isFullFlex ? "" : "overflow-hidden"}`} data-testid="app-main">
        <MainHeader
          screen={screen}
          user={user}
          setScreen={setScreen}
          onOpenMobileNav={() => setMobileNavOpen(true)}
        />
        <div className={`flex-1 min-h-0 flex overflow-hidden`}>
          <div className={`flex-1 min-w-0 ${isFullFlex ? "flex flex-col" : "overflow-y-auto subtle-scroll"}`} data-testid={`view-${screen}`}>
            {renderScreen()}
          </div>
          {showRightPanel && (
            <RightPanel user={user} device={device} go={go}/>
          )}
        </div>
      </main>

      {/* Floating Focus Mode launcher — visible on all screens, but not
          while the immersive overlay itself is open. Softer translucent
          deep-teal so it doesn't compete with the primary "Talk to Rammy"
          black CTA in the sidebar. */}
      {!focusModeOpen && (
        <button
          type="button"
          onClick={() => setFocusModeOpen(true)}
          className="group fixed right-5 bottom-24 z-40 inline-flex items-center gap-2 rounded-full backdrop-blur-md text-white pl-3 pr-4 py-2.5 shadow-[0_10px_28px_-8px_rgba(36,30,102,0.35)] ring-1 ring-white/10 hover:pr-5 transition-[padding,background] duration-200"
          style={{ backgroundColor: "var(--rm-brand)" }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--rm-brand-hover)")}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--rm-brand)")}
          data-testid="focus-mode-launcher"
          aria-label="Enter Focus mode"
          title="Focus mode"
        >
          <span className="w-6 h-6 rounded-full bg-white/15 flex items-center justify-center">
            <Sparkles size={13} className="text-white"/>
          </span>
          <span className="text-[13px] font-medium tracking-wide">Focus mode</span>
        </button>
      )}

      {focusModeOpen && (
        <FocusMode user={user} onClose={() => setFocusModeOpen(false)}/>
      )}

      {/* First-run onboarding overlay — shown until the user completes or skips.
          We gate on the presence of `onboarding_completed_at` on the user doc,
          which is set by /api/onboarding/complete or /api/onboarding/skip. */}
      {user && !user.onboarding_completed_at && (
        <OnboardingFlow
          user={user}
          onFinish={() => refresh?.()}
          onSkip={() => refresh?.()}
        />
      )}

      {/* Rammy's "living presence" toasts — proactive check-ins, due
          reminders, ambient nudges. Mounted once so ANY screen can surface
          a ping via pushRammyPing(). */}
      <RammyPingHost/>
    </div>
  );
}
